/**
 * Light/dark appearance.
 *
 * The stored preference is one of three values, but the *resolved* mode is only
 * ever light or dark. Keeping those separate is what lets "System" stay live: a
 * merchant whose iPad switches to dark at sunset sees the app follow, rather
 * than being frozen to whatever the OS was set to when they first opened it.
 *
 * Dark mode is applied by putting `dark` on the document root, which is what the
 * `.dark:root` block in `global.css` selects. Every `dark:` utility in every
 * component then switches with it, so no component reads the mode to pick a
 * colour.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useColorScheme } from 'react-native';

import { usePersistentState } from '../lib/hooks';
import { paletteFor, type Palette, type ThemeMode } from '../lib/theme';

/** What the user chose. `system` defers to the operating system. */
export type ThemePreference = ThemeMode | 'system';

const STORAGE_KEY = 'yummy.appearance.v1';

interface AppearanceValue {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** The mode actually in force, after resolving `system`. */
  mode: ThemeMode;
  /** What the operating system reports, regardless of the preference. */
  systemMode: ThemeMode;
  /** JavaScript colours for the resolved mode; see `lib/theme.ts`. */
  palette: Palette;
}

const AppearanceContext = createContext<AppearanceValue>({
  preference: 'system',
  setPreference: () => undefined,
  mode: 'light',
  systemMode: 'light',
  palette: paletteFor('light'),
});

/**
 * Reads and changes the appearance preference.
 *
 * @returns The stored preference, the resolved mode, and its palette.
 */
export function useAppearance(): AppearanceValue {
  return useContext(AppearanceContext);
}

/**
 * The colour values for the active mode.
 *
 * Styling goes through `className`; this exists only for the places a class
 * cannot reach — an SVG chart fill, a QR code's foreground, a canvas.
 *
 * @returns The resolved mode and its palette.
 */
export function useTheme(): { mode: ThemeMode; palette: Palette } {
  const { mode, palette } = useContext(AppearanceContext);
  return { mode, palette };
}

/**
 * Holds the appearance preference for one app.
 *
 * The preference is stored per app rather than per account, because it describes
 * the device in front of you: the same merchant may want dark on the counter
 * iPad and light on their phone.
 *
 * @param props Children and an optional storage key.
 */
export function AppearanceProvider({
  children,
  storageKey = STORAGE_KEY,
}: {
  children: ReactNode;
  storageKey?: string;
}) {
  const [stored, setPreference] = usePersistentState<ThemePreference>(storageKey, 'system');
  const scheme = useColorScheme();

  // `system` is the default, so an unset or unrecognised value falls back to it
  // rather than to a hardcoded light theme.
  const preference: ThemePreference =
    stored === 'light' || stored === 'dark' ? stored : 'system';

  const systemMode: ThemeMode = scheme === 'dark' ? 'dark' : 'light';
  const mode: ThemeMode = preference === 'system' ? systemMode : preference;
  const palette = useMemo(() => paletteFor(mode), [mode]);

  // `dark` drives every `dark:` utility; the background keeps the area outside
  // the app — and the iOS overscroll gutter — from staying white in dark mode.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', mode === 'dark');
    document.documentElement.style.backgroundColor = palette.background;
    document.body.style.backgroundColor = palette.background;
    document.documentElement.style.colorScheme = mode;
  }, [mode, palette]);

  const value = useMemo<AppearanceValue>(
    () => ({ preference, setPreference, mode, systemMode, palette }),
    [preference, setPreference, mode, systemMode, palette]
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
